package com.neurobs.ccabserver.translation.states

import com.neurobs.ccab.db.TranslationData
import com.neurobs.ccabserver.translation.util.{EnginePhraseTranslation, PhraseTranslationCache}
import com.neurobs.js.ccab.TranslationServer
import com.neurobs.js.util.ServerResponseUtil
import com.neurobs.react.{GenericState, GenericStateGenerator, RegisteredChangeAttribute, ServerDataAttribute}
import org.scalajs.dom.console
import slinky.core.FunctionalComponent
import slinky.core.annotations.react
import slinky.core.facade.Hooks._
import slinky.core.facade.ReactElement

case class EngineTranslationState (
                                    phraseTranslationCache: PhraseTranslationCache,
                                    translationState: ServerDataAttribute[TranslationData],
                                    registeredChangeState: RegisteredChangeAttribute
                                  ) extends GenericState {

  def registerChange() = registeredChangeState.register()

  def translationEngineId: Long = phraseTranslationCache.translationEngine.id
  def translationEngineIdentifier: String = phraseTranslationCache.translationEngine.identifier
  def phraseId: Long = phraseTranslationCache.phrase.id
  def translation = translationState.value

  def translationData = translationState.value
  def setTranslationData(translationData: TranslationData) = {
    translationState.setData(translationData)
    phraseTranslationCache.setTranslation(translationData)
    registerChange()
  }
  def clearTranslation() = translationState.reset()

  def phrase = phraseTranslationCache.phrase
  def translationEngine = phraseTranslationCache.translationEngine

  def awaitingServerTranslation = translationState.loading

  def loadServerTranslation(languageId: Long) = {
    TranslationServer.getEnginePhraseTranslation(phraseId, translationEngineId, languageId)(handleServerTranslation)
  }

  def handleServerTranslation = ServerResponseUtil.handleObjectResultResponse[TranslationData](
    handleObject = setTranslationData(_),
    onAnyError = _ => translationState.setError("There was an error loading the engine translation")
  )

}

object EngineTranslationStateGenerator extends GenericStateGenerator[EngineTranslationState] {
  override def generateEmptyDefaultState(): EngineTranslationState = EngineTranslationState(
    EnginePhraseTranslation(),
    emptyServerDataState[TranslationData](),
    emptyRegisteredChangeState()
  )
}

@react
object EngineTranslationStateProvider {

  case class Props(phraseTranslationCache: PhraseTranslationCache, children: ReactElement*)

  val component = FunctionalComponent[Props] { props =>

    def phraseTranslationCache = props.phraseTranslationCache

    val translatorState = TranslatorStateGenerator.accessState

    val translationState = ServerDataAttribute(useState[Either[Option[String], TranslationData]](Left(None)))
    val registeredChangeState = RegisteredChangeAttribute(useState(0))

    val engineTranslationState = EngineTranslationState(
      phraseTranslationCache = phraseTranslationCache,
      translationState = translationState,
      registeredChangeState = registeredChangeState
    )

    useEffect(() => {
      translatorState.selectedTranslator match {
        case None =>
        case Some(translator) =>
          phraseTranslationCache.existingTranslation match {
            case None => engineTranslationState.loadServerTranslation(translator.languageId)
            case Some(cachedTranslation) => engineTranslationState.setTranslationData(cachedTranslation)
          }
      }
    }, Vector())

    useEffect(() => {

      translatorState.selectedTranslator match {
        case None =>
        case Some(translator) =>
          phraseTranslationCache.existingTranslation match {
            case None =>
              engineTranslationState.clearTranslation()
              engineTranslationState.loadServerTranslation(translator.languageId)
            case Some(translation) =>
              engineTranslationState.setTranslationData(translation)
          }
      }
    }, Vector(phraseTranslationCache))

    EngineTranslationStateGenerator.Provider(engineTranslationState)(
      props.children:_*
    )

  }

}
